import { badgeEntityName, requireManageBadgesPermission } from '../../../../../backend/features/badges/shared';
import { useBadgingTranslation } from '../../../../../ui/hooks';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { themeColorsDark } from '@juvare/common-web-ui/JuvareTheme/colors/themeColorsDark';
import { PageHeader } from '@juvare/common-web-ui/PageHeader';
import { requireAnyOrgPermission, requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { Image } from '@juvare/platform-sdk-react/ui/components';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ControlPointDuplicateIcon from '@mui/icons-material/ControlPointDuplicate';
import EditIcon from '@mui/icons-material/Edit';
import LanIconOutlined from '@mui/icons-material/LanOutlined';
import { Box, Button, Divider, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router';
import { useBadgeActions } from '../../hooks/useBadgeActions';
import { getYearsIntervalText } from '../../utils/getYearsIntervalText';
import { manageBadgesRoutes } from '../../utils/manageBadgesRoutes';
import { ShowMoreText } from '../ShowMoreText';
import { BadgeDetailsPageHeaderAnalytics } from './BadgeDetailsPageHeaderAnalytics';
export const BadgeDetailsPageHeader = ({ badge, fetchBadgeDetails }) => {
    const { t } = useBadgingTranslation();
    const navigate = useNavigate();
    const isScreenMediumUp = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const userState = useUserState();
    const { handleBadgeDetailsEdit, handleBadgeOrganizationsEdit, handleBadgeDuplicateClick } = useBadgeActions({
        badge,
        onSuccess: fetchBadgeDetails,
    });
    const canManageOrganizationMembers = requireAnySystemPermission(userState, 'all-organizations-admin') || requireAnyOrgPermission(userState, 'manage-members');
    return (React.createElement(PageHeader, { gradient: 'cyan-dark' },
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, p: 2, width: '100%' } },
            React.createElement(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' } },
                isScreenMediumUp ? (React.createElement(Button, { variant: 'text', color: 'inherit', startIcon: React.createElement(ArrowBackIcon, null), onClick: () => void navigate(manageBadgesRoutes.home('manage-badges', { base: '../' })) }, t('badges.badgeDetails.backToBadges'))) : (React.createElement(IconButton, { color: 'inherit', size: 'small', onClick: () => void navigate(manageBadgesRoutes.home('manage-badges', { base: '../' })) },
                    React.createElement(ArrowBackIcon, null))),
                requireManageBadgesPermission(userState) && (React.createElement(Box, { sx: { display: 'flex', gap: 1 } },
                    canManageOrganizationMembers && (React.createElement(Box, { sx: { display: 'flex', gap: 1 } },
                        React.createElement(Button, { startIcon: React.createElement(LanIconOutlined, null), variant: 'text', color: 'inherit', onClick: handleBadgeOrganizationsEdit }, t('badges.badgeDetails.assignedOrg', {
                            count: badge.organizationIds.length || 0,
                        })),
                        React.createElement(Divider, { orientation: 'vertical', variant: 'middle', flexItem: true, color: 'white' }))),
                    React.createElement(IconButton, { color: 'inherit', size: 'small', onClick: () => {
                            handleBadgeDuplicateClick({ base: '../' });
                        } },
                        React.createElement(ControlPointDuplicateIcon, null)),
                    React.createElement(IconButton, { color: 'inherit', size: 'small', onClick: handleBadgeDetailsEdit },
                        React.createElement(EditIcon, null))))),
            React.createElement(Stack, { direction: { xs: 'column', sm: 'row' }, gap: 2 },
                React.createElement(Box, { sx: {
                        p: 3,
                        pb: 0,
                        overflow: 'hidden',
                        aspectRatio: '270/176',
                        borderRadius: 1,
                        backgroundColor: themeColors.alphaLightSurface.base,
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: themeColors.alphaLightSurface.subtlest,
                        flexShrink: 0,
                        maxWidth: { xs: 'none', sm: 270 },
                    } },
                    React.createElement(Image, { entity: badgeEntityName, entityId: badge.id, attachmentKey: 'badgeFrontImage', sx: {
                            width: '100%',
                            borderWidth: 1,
                            borderStyle: 'solid',
                            borderColor: themeColors.border.main,
                            borderRadius: 1,
                        } })),
                React.createElement(Stack, { gap: 2, sx: { minWidth: 0, overflow: 'hidden', flexGrow: 1 } },
                    React.createElement(Typography, { variant: 'h1', color: themeColorsDark.text.bold, noWrap: true }, badge.name),
                    React.createElement(Stack, { gap: 1, direction: 'row', sx: {
                            alignItems: 'center',
                            minWidth: 0,
                        }, divider: badge.description && (React.createElement(Box, { sx: {
                                backgroundColor: themeColors.icon.disable,
                                width: '4px',
                                height: '4px',
                                borderRadius: 50,
                                flexShrink: 0,
                            } })) },
                        React.createElement(Stack, { gap: 1, direction: 'row', sx: { alignItems: 'center', flexShrink: 0 } },
                            React.createElement(CalendarMonthIcon, null),
                            React.createElement(Typography, { variant: 'body1', color: themeColorsDark.text.bold }, getYearsIntervalText(t, badge.expirationIntervalMonths))),
                        React.createElement(ShowMoreText, { title: badge.name, description: badge.description })),
                    React.createElement(BadgeDetailsPageHeaderAnalytics, { badge: badge }))))));
};
